<script>
  import { onMount } from "svelte";
  import { currentLang } from "../lib/i18n";

  export let currentPage = "home";
  export let lang = "hu";
  export let navigate = () => {};

  let showPopup = false;
  let timerId = null;

  $: isTaxi = currentPage === "taxi";

  $: checkAndTriggerPopup(currentPage);

  function checkAndTriggerPopup(page) {
    if (timerId) clearTimeout(timerId);
    showPopup = false;

    // Separate tracking so dismissing normal doesn't block taxi, and vice versa
    const popupType = page === "taxi" ? "taxi" : "normal";
    const storageKey = `atszerviz_seen_${popupType}`;
    const alreadySeen =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(storageKey) === "true";

    if (!alreadySeen) {
      timerId = setTimeout(() => {
        showPopup = true;
      }, 900);
    }
  }

  function closePopup() {
    showPopup = false;
    const popupType = isTaxi ? "taxi" : "normal";
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(`atszerviz_seen_${popupType}`, "true");
    }
  }

  function handleCtaClick() {
    closePopup();
    if (isTaxi) {
      const el = document.getElementById("taxi-booking");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate("booking");
    }
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      closePopup();
    }
  }
</script>

{#if showPopup}
  <div
    class="popup-overlay"
    on:click={closePopup}
    on:keydown={handleKeydown}
    role="dialog"
    aria-modal="true"
    aria-label={isTaxi
      ? lang === "hu"
        ? "Taxi Flotta Kedvezmény"
        : "Taxi Fleet Offer"
      : lang === "hu"
        ? "Szerviz Akció"
        : "Service Offer"}
    tabindex="-1"
  >
    <div
      class="popup-card"
      on:click|stopPropagation
      role="document"
      on:keydown={handleKeydown}
    >
      <!-- Close button (clean SVG icon, strictly no emojis) -->
      <button
        class="close-button"
        on:click={closePopup}
        aria-label={lang === "hu" ? "Bezárás" : "Close"}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div class="popup-body">
        {#if isTaxi}
          <!-- TAXI PAGE: EXCLUSIVE TAXI FLEET DISCOUNT ONLY -->
          <div class="top-tag taxi-tag">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon
                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
              ></polygon>
            </svg>
            <span
              >{lang === "hu"
                ? "KIEMELT TAXI FLOTTA AJÁNLAT"
                : "EXCLUSIVE TAXI FLEET OFFER"}</span
            >
          </div>

          <h2 class="popup-title">
            {lang === "hu"
              ? "50% Munkadíj és Gumiszerviz Kedvezmény"
              : "50% Labor & Tire Service Discount"}
          </h2>

          <p class="popup-desc">
            {lang === "hu"
              ? "Használja ki kiemelt taxi flotta kedvezményeinket sorban állás nélkül, azonnali prémium ellátással:"
              : "Take advantage of our exclusive taxi fleet discounts with zero waiting time and premium service:"}
          </p>

          <div class="cards-grid">
            <div class="feature-card taxi-feature-card">
              <div class="card-badge taxi-badge">50%</div>
              <div class="card-content">
                <h3 class="card-title">
                  {lang === "hu" ? "Olajcsere Munkadíj" : "Oil Change Labor"}
                </h3>
                <p class="card-desc">
                  {lang === "hu"
                    ? "50% kedvezmény a szerviz olajcsere munkadíjából taxi partnereinknek"
                    : "50% discount on oil change labor fee for taxi partners"}
                </p>
              </div>
            </div>

            <div class="feature-card taxi-feature-card">
              <div class="card-badge taxi-badge">50%</div>
              <div class="card-content">
                <h3 class="card-title">
                  {lang === "hu"
                    ? 'Gumicsere (16"-ig)'
                    : 'Tire Service (up to 16")'}
                </h3>
                <p class="card-desc">
                  {lang === "hu"
                    ? "50% kedvezmény a normál árakból 16 colos méretig bezárólag"
                    : "50% discount from normal rates up to 16-inch wheel size"}
                </p>
              </div>
            </div>
          </div>

          <div class="terms-note">
            {lang === "hu"
              ? "Az ajánlat kizárólag érvényes taxi engedéllyel rendelkező partnereink számára vehető igénybe."
              : "Offer exclusively valid for registered taxi partners."}
          </div>

          <button class="cta-button" on:click={handleCtaClick}>
            <span
              >{lang === "hu"
                ? "IDŐPONTFOGLALÁS TAXISOKNAK"
                : "BOOK FOR TAXI"}</span
            >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        {:else}
          <!-- HOME AND ALL OTHER PAGES: STANDARD DISCOUNT ONLY -->
          <div class="top-tag normal-tag">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span
              >{lang === "hu"
                ? "SZEZONÁLIS SZERVIZ AKCIÓ"
                : "SEASONAL SERVICE OFFER"}</span
            >
          </div>

          <h2 class="popup-title">
            {lang === "hu"
              ? "Kedvezményes Olaj- és Gumiszerviz"
              : "Discounted Oil & Tire Service"}
          </h2>

          <p class="popup-desc">
            {lang === "hu"
              ? "Gondoskodjon autója megbízhatóságáról még ma! Válassza kiemelt karbantartási és szerelési akcióinkat:"
              : "Keep your vehicle in top condition! Take advantage of our seasonal maintenance and tire specials:"}
          </p>

          <div class="cards-grid">
            <div class="feature-card normal-feature-card">
              <div class="card-badge normal-badge">30%</div>
              <div class="card-content">
                <h3 class="card-title">
                  {lang === "hu" ? "Olajcsere Munkadíj" : "Oil Change Labor"}
                </h3>
                <p class="card-desc">
                  {lang === "hu"
                    ? "30% megtakarítás a szerviz olajcsere munkadíj árából"
                    : "30% savings on oil change service labor fee"}
                </p>
              </div>
            </div>

            <div class="feature-card normal-feature-card">
              <div class="card-badge normal-badge">15%</div>
              <div class="card-content">
                <h3 class="card-title">
                  {lang === "hu" ? "Gumicsere Akció" : "Tire Service Discount"}
                </h3>
                <p class="card-desc">
                  {lang === "hu"
                    ? "15% kedvezmény komplett kerék- és gumiszerelési árainkból"
                    : "15% discount on complete tire mounting & fitting"}
                </p>
              </div>
            </div>
          </div>

          <div class="terms-note">
            {lang === "hu"
              ? "A kedvezmény online időpontfoglalás esetén érvényes minden ügyfelünk számára."
              : "Discount valid for online bookings for all customers."}
          </div>

          <button class="cta-button" on:click={handleCtaClick}>
            <span
              >{lang === "hu"
                ? "IDŐPONTFOGLALÁS MOST"
                : "BOOK AN APPOINTMENT"}</span
            >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    max-width: 100vw;
    background-color: rgba(0, 0, 0, 0.78);
    backdrop-filter: blur(6px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9998;
    padding: 1rem 0.75rem;
    animation: fadeIn 0.3s ease-out;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .popup-card {
    position: relative;
    max-width: 800px;
    width: 100%;
    border-radius: 28px;
    overflow: hidden;
    background-color: rgb(11, 18, 33);
    background-image: linear-gradient(
        135deg,
        rgba(11, 18, 33, 0.58) 0%,
        rgba(15, 23, 42, 0.64) 50%,
        rgba(10, 16, 30, 0.75) 100%
      ),
      url("/images/tirepopup.jpg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    box-shadow:
      0 25px 70px rgba(0, 0, 0, 0.75),
      0 0 50px rgba(27, 42, 75, 0.5);
    border: 1.5px solid rgba(255, 255, 255, 0.16);
    animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    box-sizing: border-box;
    margin: auto 0;
  }

  .popup-body {
    position: relative;
    z-index: 3;
    padding: 3.2rem 3rem 2.8rem;
    text-align: center;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
  }

  .top-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 1.15rem;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 1.25rem;
  }

  .taxi-tag {
    background: rgba(234, 179, 8, 0.14);
    border: 1px solid rgba(234, 179, 8, 0.42);
    color: #facc15;
  }

  .normal-tag {
    background: rgba(56, 189, 248, 0.12);
    border: 1px solid rgba(56, 189, 248, 0.38);
    color: #38bdf8;
  }

  .popup-title {
    font-size: 2.25rem;
    font-weight: 800;
    color: #ffffff;
    line-height: 1.25;
    margin: 0 0 0.85rem;
    letter-spacing: -0.01em;
  }

  .popup-desc {
    color: rgba(255, 255, 255, 0.84);
    font-size: 1.05rem;
    line-height: 1.6;
    margin: 0 auto 1.85rem;
    max-width: 600px;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.35rem;
    margin-bottom: 1.7rem;
    text-align: left;
  }

  .feature-card {
    display: flex;
    align-items: center;
    gap: 1.1rem;
    background: rgba(255, 255, 255, 0.06);
    border: 1.5px solid rgba(255, 255, 255, 0.12);
    border-radius: 18px;
    padding: 1.35rem 1.4rem;
    backdrop-filter: blur(10px);
    transition: all 0.25s ease;
  }

  .feature-card:hover {
    background: rgba(255, 255, 255, 0.09);
    transform: translateY(-2px);
  }

  .taxi-feature-card {
    border-color: rgba(234, 179, 8, 0.4);
    background: rgba(234, 179, 8, 0.05);
    box-shadow: 0 4px 20px rgba(234, 179, 8, 0.08);
  }

  .taxi-feature-card:hover {
    border-color: rgba(234, 179, 8, 0.6);
    box-shadow: 0 6px 24px rgba(234, 179, 8, 0.14);
  }

  .normal-feature-card {
    border-color: rgba(56, 189, 248, 0.35);
    background: rgba(56, 189, 248, 0.04);
    box-shadow: 0 4px 20px rgba(56, 189, 248, 0.08);
  }

  .normal-feature-card:hover {
    border-color: rgba(56, 189, 248, 0.55);
    box-shadow: 0 6px 24px rgba(56, 189, 248, 0.14);
  }

  .card-badge {
    font-size: 2.3rem;
    font-weight: 900;
    line-height: 1;
    min-width: 65px;
    flex-shrink: 0;
    letter-spacing: -0.02em;
  }

  .taxi-badge {
    color: #facc15;
    text-shadow: 0 2px 14px rgba(250, 204, 21, 0.45);
  }

  .normal-badge {
    color: #38bdf8;
    text-shadow: 0 2px 14px rgba(56, 189, 248, 0.4);
  }

  .card-content {
    flex: 1;
  }

  .card-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.3;
    margin: 0 0 0.3rem;
  }

  .card-desc {
    font-size: 0.84rem;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.38;
    margin: 0;
  }

  .terms-note {
    font-size: 0.86rem;
    color: rgba(255, 255, 255, 0.58);
    font-style: italic;
    line-height: 1.45;
    margin-bottom: 1.8rem;
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    background: linear-gradient(135deg, #facc15 0%, #eab308 50%, #ca8a04 100%);
    color: #0f172a;
    font-weight: 800;
    font-size: 1.05rem;
    letter-spacing: 0.04em;
    padding: 1.05rem 2.8rem;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 8px 26px rgba(202, 138, 4, 0.4);
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  .cta-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(202, 138, 4, 0.58);
    background: linear-gradient(135deg, #fef08a 0%, #facc15 50%, #d97706 100%);
  }

  .close-button {
    position: absolute;
    top: 1.2rem;
    right: 1.2rem;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 5;
    transition: all 0.2s ease;
  }

  .close-button:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(25px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media screen and (max-width: 768px) {
    .popup-card {
      border-radius: 22px;
      max-width: 100%;
    }

    .popup-body {
      padding: 2.2rem 1.25rem 1.8rem;
    }

    .top-tag {
      font-size: 0.75rem;
      padding: 0.35rem 0.9rem;
      margin-bottom: 1rem;
      max-width: 100%;
      white-space: normal;
      text-align: center;
    }

    .popup-title {
      font-size: 1.5rem;
      line-height: 1.25;
      margin-bottom: 0.75rem;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    .popup-desc {
      font-size: 0.9rem;
      line-height: 1.5;
      margin-bottom: 1.25rem;
    }

    .cards-grid {
      grid-template-columns: 1fr;
      gap: 0.85rem;
      margin-bottom: 1.35rem;
      width: 100%;
    }

    .feature-card {
      padding: 1.1rem 1.15rem;
      gap: 0.9rem;
      box-sizing: border-box;
      width: 100%;
    }

    .card-badge {
      font-size: 2rem;
      min-width: 55px;
      flex-shrink: 0;
    }

    .card-title {
      font-size: 1rem;
    }

    .card-desc {
      font-size: 0.82rem;
    }

    .terms-note {
      font-size: 0.8rem;
      margin-bottom: 1.35rem;
    }

    .cta-button {
      padding: 0.95rem 1.4rem;
      font-size: 0.92rem;
      max-width: 100%;
      width: 100%;
      white-space: normal;
      box-sizing: border-box;
    }

    .close-button {
      top: 0.85rem;
      right: 0.85rem;
      width: 36px;
      height: 36px;
    }
  }

  @media screen and (max-width: 480px) {
    .popup-overlay {
      padding: 0.75rem 0.5rem;
    }

    .popup-body {
      padding: 2rem 0.9rem 1.5rem;
    }

    .popup-title {
      font-size: 1.3rem;
    }

    .feature-card {
      padding: 0.95rem 0.9rem;
      gap: 0.75rem;
    }

    .card-badge {
      font-size: 1.8rem;
      min-width: 48px;
    }

    .card-title {
      font-size: 0.95rem;
    }

    .card-desc {
      font-size: 0.78rem;
    }

    .cta-button {
      font-size: 0.86rem;
      padding: 0.9rem 1rem;
      gap: 0.4rem;
    }
  }
</style>
